import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';

const GAP = 8; // between trigger and panel
const MARGIN = 8; // minimum distance from any viewport edge

export interface AnchoredPosition {
  top: number;
  left: number;
  placement: 'below' | 'above';
}

interface AnchorBox { top: number; bottom: number; left: number; width: number }
interface PanelBox { width: number; height: number }
interface Viewport { width: number; height: number }

// Pure layout maths, exported so it can be tested without a real viewport —
// jsdom reports zeroes for every rect, which would make a DOM-level test
// assert nothing (see the OH-75 note on tests that hold nothing).
//
// Horizontal collisions are resolved by SHIFTING, never by narrowing: the
// calendar popover's two-month layout hangs on a 6px margin above
// TWO_MONTH_MIN_WIDTH, so any width clamp silently drops it to one month.
export function computeAnchoredPosition(
  anchor: AnchorBox,
  panel: PanelBox,
  viewport: Viewport,
): AnchoredPosition {
  const roomBelow = viewport.height - anchor.bottom - GAP - MARGIN;
  const roomAbove = anchor.top - GAP - MARGIN;

  const belowRawTop = anchor.bottom + GAP;
  const aboveRawTop = anchor.top - GAP - panel.height;

  // "Fits" is asked the way the panel will actually be rendered, not by
  // comparing raw gaps: clamp `top` to MARGIN first (the backstop a few
  // lines down applies unconditionally anyway), THEN check whether the
  // bottom edge lands inside the viewport. OH-131 fix round 1: the old rule
  // ("more room" — `roomBelow >= roomAbove`) picked `below` for having the
  // bigger gap even when that gap still wasn't big enough — at a
  // /search-style anchor (roomBelow 377px, roomAbove 69px, a ~454px panel),
  // `below` "won" on room alone and then ran 77px off the bottom of the
  // viewport. Checking the CLAMPED bottom directly asks the question that
  // actually matters — does the guest see the whole panel — instead of
  // "which gap is numerically bigger".
  function fitsClamped(rawTop: number): boolean {
    return Math.max(MARGIN, rawTop) + panel.height <= viewport.height - MARGIN;
  }
  const belowFits = fitsClamped(belowRawTop);
  const aboveFits = fitsClamped(aboveRawTop);

  // Prefer `below` whenever it fits — unchanged from today's default even
  // on the (common) case where `above` would ALSO fit; only reach for
  // `above` when `below` genuinely doesn't. When NEITHER fits (the panel is
  // taller than the viewport can hold on either side), fall back to the
  // original "bigger gap" heuristic — `top` is clamped below regardless, so
  // this last-resort case can still hand back a bottom-clipped panel, same
  // as before this fix, but never a negative top.
  //
  // When `above` is chosen specifically because `below` doesn't fit but
  // `above`'s CLAMPED placement does (fitsClamped's whole reason for
  // existing), the clamp can land the panel ON TOP OF its own trigger, not
  // above it: the trigger is wherever it is on the page, but a tall panel
  // clamped to MARGIN starts near the top of the VIEWPORT regardless, so
  // the two can overlap (e.g. trigger at y=85..138, panel clamped to
  // y=8..462 — see the `useAnchoredPosition.test.ts` case this describes).
  // That's deliberate, not a bug to fix here: the alternative is the
  // bottom-clipped panel this fix round exists to remove — the guest loses
  // the calendar's trailing week rows (and the nights-summary/Clear row
  // below them) instead of losing sight of the trigger button, which is
  // already pointless to see once the panel is open over it. Covering the
  // trigger beats hiding part of the panel.
  const placement: 'below' | 'above' = belowFits
    ? 'below'
    : aboveFits
      ? 'above'
      : roomBelow >= roomAbove
        ? 'below'
        : 'above';

  const rawTop = placement === 'below' ? belowRawTop : aboveRawTop;
  // Lower-bound regardless of which branch fired (OH-131): `above`
  // subtracts the full panel height from anchor.top and goes negative the
  // moment the panel is taller than the room actually above it — exactly
  // the reported bug (month caption + prev/next arrows pushed off the top
  // of the screen, unreachable). Short viewports are now routed to the
  // sheet presentation before this function is ever called (see
  // useMediaQuery.ts's useShouldPresentAsSheet), and the fit-aware
  // selection above means this only has to act as a backstop for the
  // fits-neither fallback — but it stays unconditional: this function
  // should never be able to hand back a top that clips the panel
  // off-screen, for any input. MARGIN, not 0, for the same reason `left`
  // below clamps to MARGIN rather than the bare edge — a panel flush
  // against the viewport edge with no breathing room reads as a layout bug
  // in its own right.
  const top = Math.max(MARGIN, rawTop);

  const maxLeft = viewport.width - panel.width - MARGIN;
  const left = Math.max(MARGIN, Math.min(anchor.left, maxLeft));

  return { top, left, placement };
}

// Used by `measure` below to bail out of `setPosition` when a recompute
// (e.g. a scroll-event burst, since `measure` is wired to `scroll` capture)
// lands on the exact same position: without this, every scroll tick
// re-renders `DateRangePicker` → `DateRangeCalendar` → ~60-70 day cells with
// freshly-identified predicate closures, even though nothing visibly moved.
// Exported (not inlined into `measure`) so it can be unit-tested without
// mounting the hook — jsdom reports zero-valued rects for every element, so
// exercising the bail-out through an actual scroll event would only prove
// "still zero", never distinguish "recomputed and it changed" from
// "recomputed and we skipped the render".
export function samePosition(prev: AnchoredPosition | null, next: AnchoredPosition): boolean {
  return (
    prev !== null &&
    prev.top === next.top &&
    prev.left === next.left &&
    prev.placement === next.placement
  );
}

// Positions a portaled panel against its trigger. Returns null until the
// panel has been measured — callers render it hidden until then so it never
// flashes at the document origin.
//
// Recomputes on scroll and resize (the panel is `position: fixed`, so it
// does not follow the anchor on its own) and on the panel's OWN resize: the
// calendar arrives as a lazy chunk, so the panel is a 380px skeleton first
// and a full month grid a moment later — a one-shot measurement would leave
// a flipped-up panel anchored to the wrong height.
export function useAnchoredPosition(
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  open: boolean,
): AnchoredPosition | null {
  const [position, setPosition] = useState<AnchoredPosition | null>(null);

  const measure = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const next = computeAnchoredPosition(
      { top: a.top, bottom: a.bottom, left: a.left, width: a.width },
      { width: p.width, height: p.height },
      { width: window.innerWidth, height: window.innerHeight },
    );
    // Functional update, returning the SAME `prev` reference when nothing
    // moved: React bails out of the re-render on an unchanged (Object.is)
    // state value, so a scroll/resize burst that lands on an identical
    // position costs nothing beyond this comparison.
    setPosition((prev) => (samePosition(prev, next) ? prev : next));
  }, [anchorRef, panelRef]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    measure();

    window.addEventListener('resize', measure);
    // capture: a scroll inside ANY ancestor moves the anchor, and scroll
    // events from nested scrollers do not bubble.
    window.addEventListener('scroll', measure, { capture: true, passive: true });

    const panel = panelRef.current;
    // Non-null assertion on `panel` in `ro.observe` would be forbidden by
    // this project's eslint strict config (@typescript-eslint/no-non-null-
    // assertion, part of tseslint.configs.strict) — guard instead.
    let ro: ResizeObserver | null = null;
    if (panel && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(panel);
    }

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, { capture: true });
      ro?.disconnect();
    };
  }, [open, measure, panelRef]);

  return position;
}
